import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { calculateNextMinBid, getBidIncrement } from '../../services/bidding.service';
import { submitBid } from '../../services/bidOutcome';
import { useBidOutcomeNotice } from './useBidOutcomeNotice';
import { BidOutcomeNotice } from './BidOutcomeNotice';
import { Auction, AuctionLot } from '../../types/auction.types';
import { firstContentImage } from '../../utils/lotImages';
import {
  formatTimeRemaining,
  getTimeRemaining,
  isInSoftCloseWindow,
  DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
} from '../../utils/lotTiming';
import { registrationClosedText, effectiveLotEnd } from '../../utils/saleState';

/**
 * Bid-from-tile modal. Opens over the auction grid so buyers can place a bid
 * without leaving the browse view. Signed-in bidders only: there is NO guest
 * path — a signed-out visitor is sent to sign in / register. Every bid goes
 * through placeBid(), which enforces sale registration and a card on file.
 */

interface Props {
  auction: Auction;
  lot: AuctionLot;
  onClose: () => void;
  onBidPlaced?: (lotId: string, amountCents: number) => void;
}

const fmt = (cents?: number | null) => `$${((cents ?? 0) / 100).toFixed(2)}`;

const BidFromTileModal: React.FC<Props> = ({ auction, lot, onClose, onBidPlaced }) => {
  const { currentUser, userProfile } = useAuth();

  const currentBidCents = lot.currentBid ?? lot.startingBid ?? 0;
  const hasBids = (lot.bidCount ?? 0) > 0;
  const nextMinCents = hasBids ? calculateNextMinBid(currentBidCents, auction.bidIncrements) : (lot.startingBid ?? 0);
  const incrementCents = getBidIncrement(currentBidCents, auction.bidIncrements);

  const [bidCents, setBidCents] = useState<number>(nextMinCents);
  // The lot is live, so the minimum can rise while the modal is open (someone else bids).
  // Never leave the amount below the new minimum — that would only produce a refusal.
  useEffect(() => {
    setBidCents((c) => (c < nextMinCents ? nextMinCents : c));
  }, [nextMinCents]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const { notice: outcome, show: showOutcome, clear: clearOutcome } = useBidOutcomeNotice(auction.id, lot.id);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ONE effective end time for every surface: lotEndTime (actualEndTime, else scheduledEndTime),
  // which accepts Firestore Timestamps. The 18 Oct fault was `new Date(Timestamp)` here —
  // Invalid Date -> 0 seconds -> CLOSED on lots that were still open. (Fault present on the home
  // page since 22 Jul 2026; this commit is the end-time fix ALONE, no attempt logging.)
  // DISPLAY ONLY (Oct 2026): this clock never refuses or hides a bid. The earlier of the lot's end
  // and the sale's hard stop (effectiveLotEnd). The server decides whether a bid is in time.
  const endsAt = effectiveLotEnd(lot, auction);
  const remaining = endsAt ? getTimeRemaining(endsAt) : 0;
  const inSoft = endsAt ? isInSoftCloseWindow(endsAt, auction.softCloseWindow ?? DEFAULT_SOFT_CLOSE_WINDOW_SECONDS) : false;
  const pastClock = !!endsAt && remaining <= 0;
  // Registration closed / Bid Off: a signed-out visitor cannot become a bidder here (no guest form).
  const regClosed = !currentUser ? registrationClosedText(auction) : '';

  const isMyBid = !!(currentUser && lot.currentBidder === currentUser.uid);

  const submit = async () => {
    setError('');
    clearOutcome();
    // Not a refusal: there is no account to bid with. (No guest path exists.)
    if (!currentUser) {
      setError(regClosed || 'Please sign in to bid.');
      return;
    }
    // ATTEMPT — the server decides. No check here of the clock, the minimum or "closed".
    setSubmitting(true);
    const displayName = userProfile?.displayName || userProfile?.email || 'Bidder';
    const o = await submitBid({ auctionId: auction.id, lotId: lot.id, amountCents: bidCents, bidderId: currentUser.uid, bidderName: displayName });
    setSubmitting(false);
    if (o.kind === 'accepted') {
      setSuccess(true);
      onBidPlaced?.(lot.id, bidCents);
      setTimeout(onClose, 1500);
      return;
    }
    showOutcome(o, bidCents, lot.lotNumber);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:w-[420px] sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b">
          {/* First CONTENT photo (_01…), never the _00 barcode reference frame. */}
          {firstContentImage(lot.images) && (
            <img
              src={firstContentImage(lot.images)}
              alt={lot.title}
              className="w-16 h-16 object-cover rounded"
              loading="lazy"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500">Lot {lot.lotNumber}</p>
            <h3 className="text-sm font-semibold line-clamp-2">{lot.title}</h3>
            <p className={`text-xs mt-0.5 font-mono ${inSoft ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
              {!endsAt ? 'Closing time not showing' : pastClock ? 'Closing… the official clock decides' : `Closes in ${formatTimeRemaining(remaining)}`}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-800 text-xl leading-none -mt-1"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-600">Current bid</span>
            <span className="text-2xl font-bold">{fmt(currentBidCents)}</span>
          </div>
          {isMyBid && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-1.5">
              You're the high bidder.
            </div>
          )}

          {regClosed && (
            <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">{regClosed}</p>
          )}
          {/* Not signed in: no guest path — sign in or register first. */}
          {!currentUser && !regClosed && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              To bid, sign in or create a bidder account. Every bidder registers and puts a card on
              file before bidding.{' '}
              <Link
                to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                className="font-semibold underline"
              >
                Sign in
              </Link>
              {' · '}
              <Link to="/signup" className="font-semibold underline">Create a bidder account</Link>
            </div>
          )}

          {/* Quick bid: next minimum only. The +$1/+$2/+$5 presets were removed for parity
              with BidPanel — typing an exact amount below is the deliberate jump path. This
              chip only PREFILLS the amount; the bid still goes through the Confirm button. */}
          <div>
            <p className="text-xs text-gray-500 mb-1.5">Quick bid</p>
            <button
              type="button"
              onClick={() => setBidCents(nextMinCents)}
              disabled={submitting || success}
              className={`w-full py-2 text-sm font-semibold rounded border ${
                bidCents === nextMinCents
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-800'
              }`}
            >
              Next minimum: {fmt(nextMinCents)}
            </button>
          </div>

          {/* Custom amount */}
          <div>
            <p className="text-xs text-gray-500 mb-1.5">Or enter exact amount</p>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-500">$</span>
              <input
                type="number"
                step={(incrementCents / 100).toFixed(2)}
                min={(nextMinCents / 100).toFixed(2)}
                value={(bidCents / 100).toFixed(2)}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) setBidCents(Math.round(v * 100));
                }}
                disabled={submitting || success}
                className="flex-1 px-3 py-2 border rounded text-lg font-bold text-center"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Minimum {fmt(nextMinCents)} · increment {fmt(incrementCents)}
            </p>
          </div>

          {(pastClock || !endsAt) && !success && (
            <div className="text-sm text-gray-800 bg-gray-100 border border-gray-300 rounded px-3 py-2">
              {endsAt
                ? 'The countdown has reached zero. If a last-second bid extended this lot, the new time will appear here. You can still submit — our server records the exact time your bid arrives, and the official clock decides whether it was in time.'
                : 'No closing time is showing for this lot. You can still submit — our server records the exact time your bid arrives, and the official clock decides whether it was in time.'}
            </div>
          )}

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <BidOutcomeNotice outcome={outcome} onDismiss={clearOutcome} />
          {success && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">✓ Bid placed: {fmt(bidCents)}</div>}

          {/* Submit — NOT hidden or disabled by the clock. Only "no account", "already placing"
              and "already placed" disable it; whether the lot is open is the server's call. */}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || success || !currentUser}
            className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold rounded"
          >
            {submitting ? 'Placing…' : success ? 'Placed' : `Confirm bid: ${fmt(bidCents)}`}
          </button>

          <p className="text-xs text-gray-400 text-center">
            {auction.softCloseExtension && auction.softCloseWindow
              ? `Soft close: bids in the last ${auction.softCloseWindow / 60} min extend by ${auction.softCloseExtension / 60} min.`
              : 'Soft close active.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BidFromTileModal;
